import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { stores, couriers, storeReviews, audit } from '../../services/apiClient';

const REVIEW_STATUS_OPTIONS = new Set(['all', 'published', 'hidden']);
const REVIEW_SORT_BY_OPTIONS = new Set([
  'created_at',
  'updated_at',
  'rating',
  'status',
  'moderated_at',
  'restaurant_name',
  'customer_name',
  'order_number',
]);
const REVIEW_SORT_DIR_OPTIONS = new Set(['asc', 'desc']);
const REVIEW_PAGE_SIZE_OPTIONS = new Set([20, 50, 100]);
const REVIEW_AUDIT_PAGE_SIZE_OPTIONS = new Set([10, 20, 50]);

function parsePositiveInt(value, fallbackValue) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallbackValue;
  return Math.trunc(parsed);
}

function parseReviewQuery(search) {
  const params = new URLSearchParams(search || '');

  const rawStatus = String(params.get('rv_status') || 'all').trim().toLowerCase();
  const reviewStatus = REVIEW_STATUS_OPTIONS.has(rawStatus) ? rawStatus : 'all';

  const reviewSearch = String(params.get('rv_q') || '').trim();
  const reviewPage = parsePositiveInt(params.get('rv_page'), 1);

  const parsedPageSize = parsePositiveInt(params.get('rv_size'), 20);
  const reviewPageSize = REVIEW_PAGE_SIZE_OPTIONS.has(parsedPageSize) ? parsedPageSize : 20;

  const rawSortBy = String(params.get('rv_sort_by') || 'created_at').trim().toLowerCase();
  const reviewSortBy = REVIEW_SORT_BY_OPTIONS.has(rawSortBy) ? rawSortBy : 'created_at';

  const rawSortDir = String(params.get('rv_sort_dir') || 'desc').trim().toLowerCase();
  const reviewSortDir = REVIEW_SORT_DIR_OPTIONS.has(rawSortDir) ? rawSortDir : 'desc';

  return {
    reviewStatus,
    reviewSearch,
    reviewPage,
    reviewPageSize,
    reviewSortBy,
    reviewSortDir,
  };
}

function buildPaginationNumbers(currentPage, totalPages) {
  if (totalPages <= 0) return [];
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, start + 4);
  start = Math.max(1, end - 4);
  const pages = [];
  for (let value = start; value <= end; value += 1) {
    pages.push(value);
  }
  return pages;
}

export default function AdminValidations() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialQuery = parseReviewQuery(location.search);

  const [storeData, setStoreData] = useState([]);
  const [courierData, setCourierData] = useState([]);
  const [reviewData, setReviewData] = useState([]);
  const [reviewStatus, setReviewStatus] = useState(initialQuery.reviewStatus);
  const [reviewSearchInput, setReviewSearchInput] = useState(initialQuery.reviewSearch);
  const [reviewSearch, setReviewSearch] = useState(initialQuery.reviewSearch);
  const [reviewPage, setReviewPage] = useState(initialQuery.reviewPage);
  const [reviewPageSize, setReviewPageSize] = useState(initialQuery.reviewPageSize);
  const [reviewSortBy, setReviewSortBy] = useState(initialQuery.reviewSortBy);
  const [reviewSortDir, setReviewSortDir] = useState(initialQuery.reviewSortDir);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewPages, setReviewPages] = useState(0);
  const [selectedReviewIds, setSelectedReviewIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [reasonModal, setReasonModal] = useState(null);
  const [reasonInput, setReasonInput] = useState('');
  const [reasonSubmitting, setReasonSubmitting] = useState(false);
  const [reviewAuditData, setReviewAuditData] = useState([]);
  const [reviewAuditLoading, setReviewAuditLoading] = useState(true);
  const [reviewAuditError, setReviewAuditError] = useState('');
  const [reviewAuditSearchInput, setReviewAuditSearchInput] = useState('');
  const [reviewAuditSearch, setReviewAuditSearch] = useState('');
  const [reviewAuditPage, setReviewAuditPage] = useState(1);
  const [reviewAuditPageSize, setReviewAuditPageSize] = useState(20);
  const [reviewAuditTotal, setReviewAuditTotal] = useState(0);
  const [reviewAuditPages, setReviewAuditPages] = useState(0);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [storesPending, couriersPending, reviews] = await Promise.all([
        stores.list({ status: 'pending' }),
        couriers.list({ status: 'pending' }),
        storeReviews.list({
          status: reviewStatus,
          q: reviewSearch || undefined,
          page: reviewPage,
          pageSize: reviewPageSize,
          sortBy: reviewSortBy,
          sortDir: reviewSortDir,
        }),
      ]);
      setStoreData(storesPending);
      setCourierData(couriersPending);
      const reviewItems = Array.isArray(reviews)
        ? reviews
        : (Array.isArray(reviews?.items) ? reviews.items : []);
      const totalItems = Array.isArray(reviews)
        ? reviewItems.length
        : Number(reviews?.total ?? reviewItems.length);
      const totalPages = Array.isArray(reviews)
        ? (reviewItems.length > 0 ? 1 : 0)
        : Number(reviews?.pages ?? 0);

      if (totalPages > 0 && reviewPage > totalPages) {
        setReviewPage(totalPages);
        return;
      }
      if (totalPages === 0 && reviewPage !== 1) {
        setReviewPage(1);
        return;
      }

      setReviewData(reviewItems);
      setReviewTotal(Number.isFinite(totalItems) ? totalItems : reviewItems.length);
      setReviewPages(Number.isFinite(totalPages) ? totalPages : 0);
    } catch {
      setError('Erro ao carregar validacoes');
    } finally {
      setLoading(false);
    }
  }

  async function loadReviewAudit() {
    setReviewAuditLoading(true);
    setReviewAuditError('');
    try {
      const logs = await audit.list({
        entityType: 'store_review',
        actionPrefix: 'store_review',
        q: reviewAuditSearch || undefined,
        page: reviewAuditPage,
        pageSize: reviewAuditPageSize,
      });

      const auditItems = Array.isArray(logs)
        ? logs
        : (Array.isArray(logs?.items) ? logs.items : []);
      const totalItems = Array.isArray(logs)
        ? auditItems.length
        : Number(logs?.total ?? auditItems.length);
      const totalPages = Array.isArray(logs)
        ? (auditItems.length > 0 ? 1 : 0)
        : Number(logs?.pages ?? 0);

      if (totalPages > 0 && reviewAuditPage > totalPages) {
        setReviewAuditPage(totalPages);
        return;
      }
      if (totalPages === 0 && reviewAuditPage !== 1) {
        setReviewAuditPage(1);
        return;
      }

      setReviewAuditData(auditItems);
      setReviewAuditTotal(Number.isFinite(totalItems) ? totalItems : auditItems.length);
      setReviewAuditPages(Number.isFinite(totalPages) ? totalPages : 0);
    } catch {
      setReviewAuditError('Erro ao carregar historico de moderacao');
    } finally {
      setReviewAuditLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [reviewStatus, reviewSearch, reviewPage, reviewPageSize, reviewSortBy, reviewSortDir]);

  useEffect(() => {
    loadReviewAudit();
  }, [reviewAuditSearch, reviewAuditPage, reviewAuditPageSize]);

  useEffect(() => {
    const queryState = parseReviewQuery(location.search);
    setReviewStatus((prev) => (prev === queryState.reviewStatus ? prev : queryState.reviewStatus));
    setReviewSearch((prev) => (prev === queryState.reviewSearch ? prev : queryState.reviewSearch));
    setReviewSearchInput((prev) => (prev === queryState.reviewSearch ? prev : queryState.reviewSearch));
    setReviewPage((prev) => (prev === queryState.reviewPage ? prev : queryState.reviewPage));
    setReviewPageSize((prev) => (prev === queryState.reviewPageSize ? prev : queryState.reviewPageSize));
    setReviewSortBy((prev) => (prev === queryState.reviewSortBy ? prev : queryState.reviewSortBy));
    setReviewSortDir((prev) => (prev === queryState.reviewSortDir ? prev : queryState.reviewSortDir));
  }, [location.search]);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search || '');
    const nextParams = new URLSearchParams(location.search || '');
    const reviewKeys = ['rv_status', 'rv_q', 'rv_page', 'rv_size', 'rv_sort_by', 'rv_sort_dir'];
    reviewKeys.forEach((key) => nextParams.delete(key));

    if (reviewStatus !== 'all') nextParams.set('rv_status', reviewStatus);
    if (reviewSearch) nextParams.set('rv_q', reviewSearch);
    if (reviewPage > 1) nextParams.set('rv_page', String(reviewPage));
    if (reviewPageSize !== 20) nextParams.set('rv_size', String(reviewPageSize));
    if (reviewSortBy !== 'created_at') nextParams.set('rv_sort_by', reviewSortBy);
    if (reviewSortDir !== 'desc') nextParams.set('rv_sort_dir', reviewSortDir);

    const currentSearch = currentParams.toString();
    const nextSearch = nextParams.toString();
    if (currentSearch === nextSearch) return;

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  }, [
    navigate,
    location.pathname,
    location.search,
    reviewStatus,
    reviewSearch,
    reviewPage,
    reviewPageSize,
    reviewSortBy,
    reviewSortDir,
  ]);

  useEffect(() => {
    setSelectedReviewIds((prev) => {
      const visible = new Set((reviewData || []).map((row) => row.id));
      return prev.filter((id) => visible.has(id));
    });
  }, [reviewData]);

  const getReasonModalConfig = (decision) => {
    if (!decision) {
      return {
        title: 'Motivo da acao',
        description: 'Informe o motivo para registrar esta acao.',
        required: false,
      };
    }

    if (decision.type === 'store') {
      return {
        title: decision.action === 'approve' ? 'Aprovar loja' : 'Reprovar loja',
        description: 'Informe o motivo desta validacao de loja.',
        required: true,
      };
    }

    if (decision.type === 'courier') {
      return {
        title: decision.action === 'approve' ? 'Aprovar motoboy' : 'Reprovar motoboy',
        description: 'Informe o motivo desta validacao de motoboy.',
        required: true,
      };
    }

    if (decision.type === 'review') {
      const isHide = decision.action === 'hide';
      return {
        title: isHide ? 'Ocultar avaliacao' : 'Publicar avaliacao',
        description: isHide
          ? 'Descreva por que esta avaliacao deve ser ocultada.'
          : 'Se desejar, informe o motivo para republicacao.',
        required: isHide,
      };
    }

    if (decision.type === 'review-batch') {
      const isHide = decision.action === 'hide';
      return {
        title: isHide ? 'Ocultar avaliacoes em lote' : 'Publicar avaliacoes em lote',
        description: isHide
          ? 'Motivo obrigatorio para ocultar as avaliacoes selecionadas.'
          : 'Se desejar, informe o motivo para republicar as avaliacoes selecionadas.',
        required: isHide,
      };
    }

    return {
      title: 'Motivo da acao',
      description: 'Informe o motivo para registrar esta acao.',
      required: false,
    };
  };

  const handleConfirm = () => {
    if (!confirm) return;
    setReasonInput('');
    setReasonModal(confirm);
    setConfirm(null);
  };

  const handleCloseReasonModal = () => {
    if (reasonSubmitting) return;
    setReasonModal(null);
    setReasonInput('');
  };

  const handleSubmitReasonModal = async () => {
    if (!reasonModal) return;

    const reason = String(reasonInput || '').trim();
    const modalConfig = getReasonModalConfig(reasonModal);
    if (modalConfig.required && !reason) {
      window.alert('Motivo obrigatorio para continuar.');
      return;
    }

    setReasonSubmitting(true);
    try {
      if (reasonModal.type === 'store') {
        if (reasonModal.action === 'approve') await stores.approve(reasonModal.id, reason);
        if (reasonModal.action === 'reject') await stores.reject(reasonModal.id, reason);
      }

      if (reasonModal.type === 'courier') {
        if (reasonModal.action === 'approve') await couriers.approve(reasonModal.id, reason);
        if (reasonModal.action === 'reject') await couriers.reject(reasonModal.id, reason);
      }

      if (reasonModal.type === 'review') {
        const nextStatus = reasonModal.action === 'hide' ? 'hidden' : 'published';
        await storeReviews.moderate(reasonModal.id, {
          status: nextStatus,
          reason: reason || undefined,
        });
      }

      if (reasonModal.type === 'review-batch') {
        const nextStatus = reasonModal.action === 'hide' ? 'hidden' : 'published';
        const result = await storeReviews.moderateBatch({
          review_ids: reasonModal.ids || [],
          status: nextStatus,
          reason: reason || undefined,
        });
        const missingIds = Array.isArray(result?.missing_review_ids) ? result.missing_review_ids : [];
        if (missingIds.length > 0) {
          window.alert(`Algumas avaliacoes nao foram encontradas (${missingIds.length}). Atualize a lista.`);
        }
        setSelectedReviewIds([]);
      }

      setReasonModal(null);
      setReasonInput('');
      await Promise.all([load(), loadReviewAudit()]);
    } catch {
      setError('Nao foi possivel concluir a acao solicitada.');
    } finally {
      setReasonSubmitting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('pt-BR');
  };

  const applyReviewSearch = () => {
    setReviewPage(1);
    setReviewSearch(String(reviewSearchInput || '').trim());
  };

  const clearReviewSearch = () => {
    setReviewSearchInput('');
    setReviewSearch('');
    setReviewPage(1);
  };

  const applyReviewAuditSearch = () => {
    setReviewAuditPage(1);
    setReviewAuditSearch(String(reviewAuditSearchInput || '').trim());
  };

  const clearReviewAuditSearch = () => {
    setReviewAuditSearchInput('');
    setReviewAuditSearch('');
    setReviewAuditPage(1);
  };

  const formatAuditAction = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'store_review_hidden') return 'Ocultar avaliacao';
    if (normalized === 'store_review_published') return 'Publicar avaliacao';
    if (normalized === 'store_reviews_batch_hidden') return 'Ocultar em lote';
    if (normalized === 'store_reviews_batch_published') return 'Publicar em lote';
    return value || '-';
  };

  const getAuditReason = (row) => {
    const details = row?.details && typeof row.details === 'object' ? row.details : {};
    const reason = String(details.reason || '').trim();
    return reason || '-';
  };

  const getAuditResult = (row) => {
    const details = row?.details && typeof row.details === 'object' ? row.details : {};
    const processedCount = Number(details.processed_count || 0);
    const updatedCount = Number(details.updated_count || 0);
    if (!processedCount && !updatedCount) return '-';
    return `${updatedCount}/${processedCount}`;
  };

  const visibleReviewIds = (reviewData || []).map((row) => row.id).filter(Boolean);
  const allVisibleReviewsSelected = visibleReviewIds.length > 0
    && visibleReviewIds.every((id) => selectedReviewIds.includes(id));

  const toggleSelectAllVisibleReviews = () => {
    if (allVisibleReviewsSelected) {
      setSelectedReviewIds([]);
      return;
    }
    setSelectedReviewIds(visibleReviewIds);
  };

  const toggleReviewSelection = (reviewId) => {
    setSelectedReviewIds((prev) => (
      prev.includes(reviewId)
        ? prev.filter((id) => id !== reviewId)
        : [...prev, reviewId]
    ));
  };

  const paginationNumbers = buildPaginationNumbers(reviewPage, reviewPages);
  const auditPaginationNumbers = buildPaginationNumbers(reviewAuditPage, reviewAuditPages);

  const reasonModalConfig = getReasonModalConfig(reasonModal);

  return (
    <div className="admin-section">
      <PageHeader title="Avaliacoes e Validacoes" subtitle="Aprovacao de lojas e motoboys" />

      <div className="admin-card">
        <h3>Lojas pendentes</h3>
        <DataTable
          columns={[
            { key: 'name', header: 'Loja' },
            { key: 'email', header: 'Email' },
            {
              key: 'actions',
              header: 'Acoes',
              render: (row) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="admin-button secondary" onClick={() => setConfirm({ type: 'store', id: row.id, action: 'approve' })}>Aprovar</button>
                  <button className="admin-button secondary" onClick={() => setConfirm({ type: 'store', id: row.id, action: 'reject' })}>Reprovar</button>
                </div>
              )
            }
          ]}
          data={storeData}
          loading={loading}
          error={error}
          emptyMessage="Nenhuma loja pendente"
        />
      </div>

      <div className="admin-card">
        <h3>Motoboys pendentes</h3>
        <DataTable
          columns={[
            { key: 'name', header: 'Motoboy' },
            { key: 'email', header: 'Email' },
            {
              key: 'actions',
              header: 'Acoes',
              render: (row) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="admin-button secondary" onClick={() => setConfirm({ type: 'courier', id: row.id, action: 'approve' })}>Aprovar</button>
                  <button className="admin-button secondary" onClick={() => setConfirm({ type: 'courier', id: row.id, action: 'reject' })}>Reprovar</button>
                </div>
              )
            }
          ]}
          data={courierData}
          loading={loading}
          error={error}
          emptyMessage="Nenhum motoboy pendente"
        />
      </div>

      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Avaliacoes de lojas</h3>
          <div className="admin-inline-actions">
            <select
              value={reviewStatus}
              onChange={(event) => {
                setReviewPage(1);
                setReviewStatus(event.target.value);
              }}
              className="admin-input"
              style={{ maxWidth: 220 }}
            >
              <option value="all">Todas</option>
              <option value="published">Publicadas</option>
              <option value="hidden">Ocultadas</option>
            </select>
            <input
              type="text"
              className="admin-input"
              value={reviewSearchInput}
              onChange={(event) => setReviewSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyReviewSearch();
              }}
              placeholder="Buscar por loja, cliente, pedido..."
              style={{ minWidth: 240 }}
            />
            <button className="admin-button secondary" onClick={applyReviewSearch}>Buscar</button>
            <button className="admin-button secondary" onClick={clearReviewSearch}>Limpar</button>
            <select
              value={reviewPageSize}
              onChange={(event) => {
                setReviewPage(1);
                setReviewPageSize(Number(event.target.value));
              }}
              className="admin-input"
              style={{ maxWidth: 140 }}
            >
              <option value={20}>20 / pagina</option>
              <option value={50}>50 / pagina</option>
              <option value={100}>100 / pagina</option>
            </select>
            <select
              value={reviewSortBy}
              onChange={(event) => {
                setReviewPage(1);
                setReviewSortBy(event.target.value);
              }}
              className="admin-input"
              style={{ minWidth: 170 }}
            >
              <option value="created_at">Ordenar: Criacao</option>
              <option value="rating">Ordenar: Nota</option>
              <option value="status">Ordenar: Status</option>
              <option value="restaurant_name">Ordenar: Loja</option>
              <option value="customer_name">Ordenar: Cliente</option>
              <option value="order_number">Ordenar: Pedido</option>
              <option value="moderated_at">Ordenar: Moderacao</option>
            </select>
            <select
              value={reviewSortDir}
              onChange={(event) => {
                setReviewPage(1);
                setReviewSortDir(event.target.value);
              }}
              className="admin-input"
              style={{ minWidth: 120 }}
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </div>
        </div>
        <div className="admin-inline-actions" style={{ marginBottom: 10 }}>
          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={allVisibleReviewsSelected}
              onChange={toggleSelectAllVisibleReviews}
            />
            <span>Selecionar todas visiveis</span>
          </label>
          <button
            className="admin-button secondary"
            disabled={selectedReviewIds.length === 0}
            onClick={() => setConfirm({ type: 'review-batch', action: 'hide', ids: selectedReviewIds })}
          >
            Ocultar selecionadas
          </button>
          <button
            className="admin-button secondary"
            disabled={selectedReviewIds.length === 0}
            onClick={() => setConfirm({ type: 'review-batch', action: 'publish', ids: selectedReviewIds })}
          >
            Publicar selecionadas
          </button>
          <span className="admin-inline-hint">{selectedReviewIds.length} selecionada(s)</span>
        </div>

        <DataTable
          columns={[
            {
              key: 'select',
              header: 'Sel',
              render: (row) => (
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedReviewIds.includes(row.id)}
                    onChange={() => toggleReviewSelection(row.id)}
                  />
                  <span />
                </label>
              ),
            },
            { key: 'restaurant_name', header: 'Loja' },
            { key: 'customer_name', header: 'Cliente' },
            { key: 'order_number', header: 'Pedido' },
            {
              key: 'rating',
              header: 'Nota',
              render: (row) => `${row.rating || 0}/5`,
            },
            {
              key: 'comment',
              header: 'Comentario',
              render: (row) => row.comment || '-',
            },
            {
              key: 'status',
              header: 'Status',
              render: (row) => (row.status === 'hidden' ? 'Ocultada' : 'Publicada'),
            },
            {
              key: 'moderation_reason',
              header: 'Motivo moderacao',
              render: (row) => row.moderation_reason || '-',
            },
            {
              key: 'moderated_at',
              header: 'Moderada em',
              render: (row) => formatDate(row.moderated_at),
            },
            {
              key: 'actions',
              header: 'Acoes',
              render: (row) => (
                <div style={{ display: 'flex', gap: 6 }}>
                  {row.status === 'hidden' ? (
                    <button
                      className="admin-button secondary"
                      onClick={() => setConfirm({ type: 'review', id: row.id, action: 'publish' })}
                    >
                      Publicar
                    </button>
                  ) : (
                    <button
                      className="admin-button secondary"
                      onClick={() => setConfirm({ type: 'review', id: row.id, action: 'hide' })}
                    >
                      Ocultar
                    </button>
                  )}
                </div>
              ),
            },
          ]}
          data={reviewData}
          loading={loading}
          error={error}
          emptyMessage="Nenhuma avaliacao encontrada"
        />
        <div className="admin-inline-actions" style={{ justifyContent: 'space-between', marginTop: 10 }}>
          <span className="admin-inline-hint">
            Total: {reviewTotal} | Pagina {reviewPages === 0 ? 0 : reviewPage} de {reviewPages}
          </span>
          <div className="admin-inline-actions">
            <button
              className="admin-button secondary"
              disabled={reviewPage <= 1}
              onClick={() => setReviewPage(1)}
            >
              Primeira
            </button>
            <button
              className="admin-button secondary"
              disabled={reviewPage <= 1}
              onClick={() => setReviewPage((prev) => Math.max(1, prev - 1))}
            >
              Anterior
            </button>
            <div className="admin-pagination-pages">
              {paginationNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  className={`admin-button secondary admin-pagination-page ${pageNumber === reviewPage ? 'active' : ''}`}
                  onClick={() => setReviewPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              className="admin-button secondary"
              disabled={reviewPages === 0 || reviewPage >= reviewPages}
              onClick={() => setReviewPage((prev) => prev + 1)}
            >
              Proxima
            </button>
            <button
              className="admin-button secondary"
              disabled={reviewPages === 0 || reviewPage >= reviewPages}
              onClick={() => setReviewPage(reviewPages)}
            >
              Ultima
            </button>
          </div>
        </div>
      </div>

      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Historico de moderacao</h3>
          <div className="admin-inline-actions">
            <input
              type="text"
              className="admin-input"
              value={reviewAuditSearchInput}
              onChange={(event) => setReviewAuditSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyReviewAuditSearch();
              }}
              placeholder="Buscar por acao, admin ou id..."
              style={{ minWidth: 260 }}
            />
            <button className="admin-button secondary" onClick={applyReviewAuditSearch}>Buscar</button>
            <button className="admin-button secondary" onClick={clearReviewAuditSearch}>Limpar</button>
            <select
              value={reviewAuditPageSize}
              onChange={(event) => {
                const nextSize = parsePositiveInt(event.target.value, 20);
                const safeSize = REVIEW_AUDIT_PAGE_SIZE_OPTIONS.has(nextSize) ? nextSize : 20;
                setReviewAuditPage(1);
                setReviewAuditPageSize(safeSize);
              }}
              className="admin-input"
              style={{ maxWidth: 140 }}
            >
              <option value={10}>10 / pagina</option>
              <option value={20}>20 / pagina</option>
              <option value={50}>50 / pagina</option>
            </select>
          </div>
        </div>

        <DataTable
          columns={[
            {
              key: 'created_at',
              header: 'Data',
              render: (row) => formatDate(row.created_at),
            },
            {
              key: 'actor_email',
              header: 'Admin',
              render: (row) => row.actor_email || '-',
            },
            {
              key: 'action',
              header: 'Acao',
              render: (row) => formatAuditAction(row.action),
            },
            {
              key: 'entity_id',
              header: 'Avaliacao',
              render: (row) => row.entity_id || 'Lote',
            },
            {
              key: 'reason',
              header: 'Motivo',
              render: (row) => getAuditReason(row),
            },
            {
              key: 'result',
              header: 'Resultado',
              render: (row) => getAuditResult(row),
            },
          ]}
          data={reviewAuditData}
          loading={reviewAuditLoading}
          error={reviewAuditError}
          emptyMessage="Nenhum evento de moderacao encontrado"
        />

        <div className="admin-inline-actions" style={{ justifyContent: 'space-between', marginTop: 10 }}>
          <span className="admin-inline-hint">
            Total: {reviewAuditTotal} | Pagina {reviewAuditPages === 0 ? 0 : reviewAuditPage} de {reviewAuditPages}
          </span>
          <div className="admin-inline-actions">
            <button
              className="admin-button secondary"
              disabled={reviewAuditPage <= 1}
              onClick={() => setReviewAuditPage(1)}
            >
              Primeira
            </button>
            <button
              className="admin-button secondary"
              disabled={reviewAuditPage <= 1}
              onClick={() => setReviewAuditPage((prev) => Math.max(1, prev - 1))}
            >
              Anterior
            </button>
            <div className="admin-pagination-pages">
              {auditPaginationNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  className={`admin-button secondary admin-pagination-page ${pageNumber === reviewAuditPage ? 'active' : ''}`}
                  onClick={() => setReviewAuditPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              className="admin-button secondary"
              disabled={reviewAuditPages === 0 || reviewAuditPage >= reviewAuditPages}
              onClick={() => setReviewAuditPage((prev) => prev + 1)}
            >
              Proxima
            </button>
            <button
              className="admin-button secondary"
              disabled={reviewAuditPages === 0 || reviewAuditPage >= reviewAuditPages}
              onClick={() => setReviewAuditPage(reviewAuditPages)}
            >
              Ultima
            </button>
          </div>
        </div>
      </div>

      {reasonModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3 style={{ marginBottom: 8 }}>{reasonModalConfig.title}</h3>
            <p style={{ color: '#B8B8C5', marginBottom: 12 }}>{reasonModalConfig.description}</p>
            <textarea
              className="admin-input admin-textarea"
              value={reasonInput}
              onChange={(event) => setReasonInput(event.target.value)}
              placeholder={reasonModalConfig.required ? 'Motivo obrigatorio' : 'Motivo opcional'}
              maxLength={300}
            />
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span className="admin-inline-hint">{String(reasonInput || '').trim().length}/300</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="admin-button secondary" onClick={handleCloseReasonModal} disabled={reasonSubmitting}>
                  Cancelar
                </button>
                <button className="admin-button" onClick={handleSubmitReasonModal} disabled={reasonSubmitting}>
                  {reasonSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.type === 'review' || confirm?.type === 'review-batch' ? 'Confirmar moderacao' : 'Confirmar validacao'}
        description={
          confirm?.type === 'review' || confirm?.type === 'review-batch'
            ? 'Voce podera informar o motivo na proxima etapa.'
            : 'Informe um motivo para a acao.'
        }
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
